import {
  GENERIC_FACETS,
  METRICS_COLUMNS,
} from '@/Pages/DatasetSessionCompare/constants';
import { formatFacets } from '@/Pages/DatasetSessionCompare/utils/getFacetsForColumns';
import { Skeleton } from '@/components/Skeleton';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { RunFacetField, SessionSchema } from '@/types/schema';
import { capitalize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { DatasetSessionCompareFilter } from '../../DatasetSessionCompareFilter';
import { AggregatedStatHeader } from '../../ExperimentsGroupByTable/AggregatedStatHeader';
import { ExperimentColumnHeader } from '../ExperimentColumnHeader';

export const MetricsColumnHeader = ({
  metric,
  columnIndex,
  tableHeight,
  columnWidthStorage,
  setColumnWidthStorage,
  runFacets,
  currentFilters,
  onChange,
  className,
  isTable = true,
  session,
  isSessionValidating = false,
}: {
  metric: (typeof METRICS_COLUMNS)[keyof typeof METRICS_COLUMNS];
  columnIndex: number;
  tableHeight: number;
  columnWidthStorage: number[];
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  runFacets: RunFacetField[];
  currentFilters: string[];
  onChange?: (clauses: string[]) => void;
  className?: string;
  isTable?: boolean;
  session?: SessionSchema;
  isSessionValidating?: boolean;
}) => {
  const aggregatesToUse = {
    latency: { stat: session?.latency_p50, type: 'P50' },
    tokens: { stat: session?.total_tokens, type: 'TOTAL' },
    cost: { stat: session?.total_cost, type: 'TOTAL' },
  };

  const aggregate = aggregatesToUse[metric.name];
  const aggregateAvailable = aggregate !== undefined && aggregate != null;

  const allFacets = formatFacets([...runFacets, ...GENERIC_FACETS], [metric]);
  const facetsForMetric = allFacets.filter(
    (facet) => facet.key === metric.name
  );

  return (
    <ExperimentColumnHeader
      key={metric.name}
      columnIndex={columnIndex}
      columnName={metric.name}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      tableHeight={tableHeight}
      minColumnWidth={metric.minWidth}
      className={cn('h-[50px]', className)}
      isTable={isTable}
    >
      <div className={cn(!!onChange && 'flex flex-col')}>
        <div className="flex min-w-0 shrink-0 items-center gap-2 overflow-hidden break-words rounded-md">
          <TruncatedTextWithTooltip text={capitalize(metric.name)} />
        </div>
        {onChange && (
          <div
            className={cn(
              'flex items-center gap-1',
              aggregateAvailable ? 'justify-between' : 'justify-end'
            )}
          >
            {isSessionValidating ? (
              <Skeleton />
            ) : (
              <>
                {aggregateAvailable && (
                  <div className="mt-1.5 flex items-center">
                    <AggregatedStatHeader
                      stat={aggregate.stat}
                      type={aggregate.type}
                    />
                  </div>
                )}
                {facetsForMetric.length > 0 && (
                  <DatasetSessionCompareFilter
                    facets={facetsForMetric}
                    currentFilters={currentFilters}
                    onChange={onChange}
                    hideClearButton
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </ExperimentColumnHeader>
  );
};
